import { useState, useEffect, useCallback, useRef } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import classNames from 'classnames';

import { CtaButton } from '../../../../admin/shared-components/CtaButton/CtaButton';
import { ViewContainer } from '../../../shared-components/View/ViewContainer/ViewContainer';
import AddUsersSearchBox from '../../../../common/components/AddUsersSearchBox';
import { ContextMenu, MenuItemList, MenuItem } from '../../../../common/components/ContextMenu';
import styles from '../OrganizationSettings.module.css';
import { mobxInjectSelect, downloadFile } from '../../../../common/utils';
import {
  PBX_USER_LINES_TAB,
  PBXDirectoryEntry,
  PBXDirectoryResponse,
  PBXLinesList,
  PBXResponse,
  PBXTab,
  User,
} from '../../../../types';
import TCClient from '../../../../client';
import ToolTip from '../../../../widgets/messenger/components/ToolTip';
import { Input } from '../../../../admin/shared-components/Input/Input';
import { Dropdown, DropdownOption } from '../../../../admin/shared-components/Dropdown/Dropdown';
import { Pagination } from '../../../shared-components/Pagination/Pagination';
import { ReactComponent as DownloadButtonSvg } from './../../../../common/images/downloadPBX.svg';
import { ReactComponent as UploadButtonSvg } from './../../../../common/images/uploadPBX.svg';
import { ReactComponent as RefreshButtonSvg } from './../../../../common/images/refresh.svg';
import UserRoleAssigneeTable from './UserRoleAssigneeTable';
import PBXDirectory from './PBXDirectory';
import { TabProps } from './';
import { useAppSelector } from 'redux-stores';

const PAGE_SIZE = 20;

type NurseCallStatus = {
  data: {
    status: boolean;
  };
};

type SortColumn = 'extensionNumber' | 'displayName' | 'unassignedExtensionNumber';
type SortDirection = 'asc' | 'desc';
type SortValue = `${SortColumn}-${SortDirection}`;

const SORT_OPTIONS: DropdownOption<SortValue>[] = [
  { label: 'Extension Number: Lowest to highest', value: 'extensionNumber-asc' },
  { label: 'Extension Number: Highest to lowest', value: 'extensionNumber-desc' },
  { label: 'Display Name: A to Z', value: 'displayName-asc' },
  { label: 'Display Name: Z to A', value: 'displayName-desc' },
  {
    label: 'Unassigned Extension Number: Lowest to highest',
    value: 'unassignedExtensionNumber-asc',
  },
  {
    label: 'Unassigned Extension Number: Highest to lowest',
    value: 'unassignedExtensionNumber-desc',
  },
];

const DEFAULT_SORT_STATE: { column: SortColumn; direction: SortDirection } = {
  column: 'extensionNumber',
  direction: 'asc',
};

// The server is the source of truth for ordering (see PbxAPI.getPbxLines) — no client-side
// sort/reverse is applied, so results are rendered exactly as returned.

const sortFieldFor = (column: SortColumn): 'displayName' | 'extensionNumber' =>
  column === 'displayName' ? 'displayName' : 'extensionNumber';

const isAssignedFilterFor = (column: SortColumn): boolean | undefined => {
  if (column === 'unassignedExtensionNumber') return false;
  if (column === 'displayName' || column === 'extensionNumber') return true;
  return undefined;
};

const sortRequestParamsFor = (state: { column: SortColumn; direction: SortDirection }) => ({
  isAssigned: isAssignedFilterFor(state.column),
  sortField: sortFieldFor(state.column),
  sortDirection: state.direction,
});

type MobxProps = {
  openModal: (name: string, options?: unknown) => void;
  findUser: (targetId: string, organizationId: string) => Promise<User>;
  pbxSelfServiceEnabled: boolean;
};

function PBX({ onClose, openModal, findUser, pbxSelfServiceEnabled }: TabProps & MobxProps) {
  const [category, setCategory] = useState<PBXTab>(PBX_USER_LINES_TAB);
  const [isAddUsersSearchBoxOpen, setIsAddUsersSearchBoxOpen] = useState(false);
  const [nurseCallStatus, setNurseCallStatus] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pbxLinesList, setPbxLinesList] = useState<PBXLinesList[]>([]);
  const [maxPages, setMaxPages] = useState<number>(1);
  const isCategoryNurseCall =
    category !== PBX_USER_LINES_TAB && category !== 'Roles Lines' && category !== 'External';
  const isCategoryDirectory = category === 'External';
  const [directoryEntries, setDirectoryEntries] = useState<PBXDirectoryEntry[]>([]);
  const [isDirectoryLoading, setIsDirectoryLoading] = useState(false);
  const [directoryPageNumber, setDirectoryPageNumber] = useState<number>(1);
  const [directoryMaxPages, setDirectoryMaxPages] = useState<number>(1);

  const currentOrganizationId = useAppSelector((state) => state.orgSettings.settings.id);
  const [extensionSearch, setExtensionSearch] = useState('');
  const [isSearchDirty, setIsSearchDirty] = useState(false);
  const isSearchActive = extensionSearch.trim() !== '';
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [sortState, setSortState] = useState<{
    column: SortColumn;
    direction: SortDirection;
  }>(DEFAULT_SORT_STATE);
  const sortDropdownValue: SortValue = `${sortState.column}-${sortState.direction}`;

  const handleSortChange = (value: SortValue) => {
    const [column, direction] = value.split('-') as [SortColumn, SortDirection];
    setSortState({ column, direction });
    setPbxLinesList([]);
    setSelected([]);
    setPageNumber(1);
    getAllPbxLinesInOrg({
      category,
      existingLines: [],
      pageNumber: 1,
      search: isSearchActive ? extensionSearch.trim() : undefined,
      ...sortRequestParamsFor({ column, direction }),
    });
  };

  const getAllPbxLinesInOrg = useCallback(
    async ({
      category,
      existingLines,
      pageNumber,
      search,
      isAssigned,
      sortField,
      sortDirection,
    }: {
      category: string;
      existingLines: PBXLinesList[];
      pageNumber: number;
      search?: string;
      isAssigned?: boolean;
      sortField?: 'displayName' | 'extensionNumber';
      sortDirection?: SortDirection;
    }) => {
      try {
        const { data } = await TCClient.pbx.getPbxLines({
          orgId: currentOrganizationId,
          page: pageNumber,
          size: PAGE_SIZE,
          targetType:
            category === PBX_USER_LINES_TAB
              ? 'User'
              : category === 'Roles Lines'
              ? 'Role'
              : 'NurseCall',
          search,
          isAssigned,
          sortField,
          sortDirection,
        });
        const responseData = data as PBXResponse;
        const totalPages = responseData.metaData.totalPages;
        const content = responseData.content as PBXLinesList[];
        setPbxLinesList([...(existingLines || []), ...content]);
        setMaxPages(totalPages);
      } catch (err) {
        console.error(err);
      }
    },
    [currentOrganizationId]
  );

  const retrieveNurseCallUserStatus = useCallback(async () => {
    const { data } = await TCClient.pbx.retrieveNurseCallStatus({
      orgId: currentOrganizationId,
    });
    const status = data as NurseCallStatus;
    if (status) setNurseCallStatus(true);
  }, [currentOrganizationId]);

  const getPbxDirectory = useCallback(
    async ({
      page = 1,
      search = '',
      existingEntries = [],
    }: {
      page?: number;
      search?: string;
      existingEntries?: PBXDirectoryEntry[];
    } = {}) => {
      setIsDirectoryLoading(true);
      try {
        const response = await TCClient.pbx.getPbxDirectory({
          orgToken: currentOrganizationId,
          page,
          size: PAGE_SIZE,
          search,
        });
        const responseData = response?.data as PBXDirectoryResponse;
        const entries = responseData.content;
        setDirectoryEntries([...existingEntries, ...entries]);
        setDirectoryMaxPages(responseData.metaData.totalPages);
        setDirectoryPageNumber(page);
      } catch (err) {
        console.error('Failed to fetch PBX directory info', err);
      } finally {
        setIsDirectoryLoading(false);
      }
    },
    [currentOrganizationId]
  );

  useEffect(() => {
    setExtensionSearch('');
    setIsSearchDirty(false);
    if (category === 'External') {
      setDirectoryEntries([]);
      setDirectoryPageNumber(1);
      getPbxDirectory({ page: 1, search: '' });
      return;
    }
    setPbxLinesList([]);
    setSelected([]);
    setPageNumber(1);
    setSortState(DEFAULT_SORT_STATE);
    getAllPbxLinesInOrg({
      category,
      existingLines: [],
      pageNumber: 1,
      ...sortRequestParamsFor(DEFAULT_SORT_STATE),
    });
    if (category === 'Nurse Call Users') retrieveNurseCallUserStatus();
  }, [
    currentOrganizationId,
    getAllPbxLinesInOrg,
    getPbxDirectory,
    category,
    retrieveNurseCallUserStatus,
  ]);

  useEffect(() => {
    const trimmed = extensionSearch.trim();

    if (isCategoryDirectory) {
      if (trimmed) {
        setIsSearchDirty(true);
        setDirectoryEntries([]);
        setDirectoryPageNumber(1);
        getPbxDirectory({ page: 1, search: trimmed });
      } else if (isSearchDirty) {
        setDirectoryEntries([]);
        setDirectoryPageNumber(1);
        getPbxDirectory({ page: 1, search: '' });
        setIsSearchDirty(false);
      }
      return;
    }

    if (trimmed) {
      setPbxLinesList([]);
      setSelected([]);
      setPageNumber(1);
      setIsSearchDirty(true);
      setSortState(DEFAULT_SORT_STATE);
      getAllPbxLinesInOrg({
        category,
        existingLines: [],
        pageNumber: 1,
        search: trimmed,
        ...sortRequestParamsFor(DEFAULT_SORT_STATE),
      });
      return;
    }

    if (isSearchDirty) {
      setPbxLinesList([]);
      setSelected([]);
      setPageNumber(1);
      setSortState(DEFAULT_SORT_STATE);
      getAllPbxLinesInOrg({
        category,
        existingLines: [],
        pageNumber: 1,
        ...sortRequestParamsFor(DEFAULT_SORT_STATE),
      });
      setIsSearchDirty(false);
    }
  }, [
    category,
    extensionSearch,
    getAllPbxLinesInOrg,
    getPbxDirectory,
    isCategoryDirectory,
    isSearchDirty,
  ]);

  const unassignUsersOrRoles = (selected: string[]) => {
    openModal('removeRoleOrUserLine', {
      currentOrganizationId,
      lineType: category,
      pbxLinesList,
      getAllPbxLinesInOrg,
      findUser,
      selectedLines: selected,
      setPbxLinesList,
      setSelected,
    });
  };

  const toggleSelected = (token: string) => {
    if (selected.includes(token)) {
      setSelected(selected.filter((id) => id !== token));
    } else {
      setSelected([...selected, token]);
    }
  };

  const downloadCSV = () => {
    const csvContent = 'extension,assignee_token\n';
    downloadFile('role_users_template.csv', 'text/csv', csvContent);
  };

  const downloadLineDetailsCSV = () => {
    const csvContent = [
      'User Name,Extension,First Name,Last Name,Caller ID Number,Emergency Caller ID,Forward Always Enabled,Forward Always Destination',
      '',
    ].join('\n');
    downloadFile('line_details_template.csv', 'text/csv', csvContent);
  };

  const refreshCurrentPage = () => {
    getAllPbxLinesInOrg({
      category,
      existingLines: [],
      pageNumber,
      search: isSearchActive ? extensionSearch.trim() : undefined,
      ...sortRequestParamsFor(sortState),
    });
  };

  const refreshLines = () => {
    if (isCategoryDirectory) {
      setDirectoryEntries([]);
      setDirectoryPageNumber(1);
      getPbxDirectory({ page: 1, search: extensionSearch.trim() });
      return;
    }
    setPbxLinesList([]);
    setSelected([]);
    setPageNumber(1);
    getAllPbxLinesInOrg({
      category,
      existingLines: [],
      pageNumber: 1,
      search: isSearchActive ? extensionSearch.trim() : undefined,
      ...sortRequestParamsFor(sortState),
    });
  };

  return (
    <ViewContainer
      {...{
        header: 'PBX Lines',
        onClose,
      }}
    >
      <div>
        <div className={styles.refreshButton} onClick={refreshLines}>
          <ToolTip text="Refresh lines" textAlign={'center'} location={'bottom'}>
            <RefreshButtonSvg />
          </ToolTip>
        </div>
        <div className={styles.pbxOuterContainer}>
          <div className={styles.pbxLinesContainer}>
            {([PBX_USER_LINES_TAB, 'Roles Lines', 'Nurse Call Users', 'External'] as PBXTab[]).map(
              (line) => {
                return (
                  <button
                    className={classNames(
                      styles.titleButton,
                      category === line && styles.titleButtonActive
                    )}
                    data-test-id={line.split(' ').join('-').toLowerCase()}
                    onClick={() => setCategory(line)}
                    key={line}
                  >
                    {line}
                  </button>
                );
              }
            )}
            <div className={styles.pbxLinesSearchContainer}>
              <Input
                shouldFocus={false}
                inputRef={searchInputRef}
                onInputChange={(e) => setExtensionSearch(e.target.value)}
                value={extensionSearch}
                hasClearButton={true}
                onClearClick={() => setExtensionSearch('')}
                dataTestId="pbx-search"
              />
            </div>
          </div>
        </div>
        {isCategoryDirectory ? (
          <>
            <PBXDirectory
              organizationId={currentOrganizationId}
              openModal={openModal}
              directoryEntries={directoryEntries}
              isLoading={isDirectoryLoading}
              getPbxDirectory={() => getPbxDirectory({ page: 1, search: extensionSearch.trim() })}
            />
            {directoryPageNumber < directoryMaxPages && (
              <div className={styles.outerContainerButton}>
                <CtaButton
                  label={'Load More'}
                  onClick={() => {
                    const nextPage = directoryPageNumber + 1;
                    getPbxDirectory({
                      page: nextPage,
                      search: extensionSearch.trim(),
                      existingEntries: directoryEntries,
                    });
                  }}
                  isInsertButton={true}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <div>
              <div className={styles.outerFlexContainer}>
                <div className={styles.numbersHeader}>
                  {isCategoryNurseCall && <div>Nurse Call Users</div>}
                  {isCategoryNurseCall && nurseCallStatus && (
                    <div
                      className={styles.unassignUsersButton}
                      onClick={() => setIsAddUsersSearchBoxOpen(true)}
                    >
                      Add Users
                    </div>
                  )}
                  {isAddUsersSearchBoxOpen && (
                    <div>
                      <OutsideClickHandler onOutsideClick={() => setIsAddUsersSearchBoxOpen(false)}>
                        <AddUsersSearchBox
                          category={category}
                          setIsAddUsersSearchBoxOpen={setIsAddUsersSearchBoxOpen}
                          organizationId={currentOrganizationId}
                          getAllPbxLinesInOrg={getAllPbxLinesInOrg}
                        />
                      </OutsideClickHandler>
                    </div>
                  )}
                </div>
                {category === PBX_USER_LINES_TAB && selected.length === 0 && (
                  <div className={styles.bulkUpload}>
                    <div className={styles.fileUpload}>
                      {pbxSelfServiceEnabled ? (
                        <ContextMenu
                          event="click"
                          position="bottomright"
                          softClick={false}
                          offsetY={4}
                          menu={
                            <MenuItemList>
                              <MenuItem onClick={() => openModal('uploadPBXLines')}>
                                Assign Roles &amp; Users
                              </MenuItem>
                              <MenuItem onClick={() => openModal('uploadPBXLineDetails')}>
                                Update Line Details
                              </MenuItem>
                            </MenuItemList>
                          }
                        >
                          <button className={styles.uploadButton}>
                            <div className={styles.buttonSvg}>
                              <UploadButtonSvg />
                            </div>
                            Click to upload
                          </button>
                        </ContextMenu>
                      ) : (
                        <button
                          className={styles.uploadButton}
                          onClick={() => openModal('uploadPBXLines')}
                        >
                          <div className={styles.buttonSvg}>
                            <UploadButtonSvg />
                          </div>
                          Click to upload
                        </button>
                      )}
                    </div>
                    <div className={styles.fileUpload}>
                      {pbxSelfServiceEnabled ? (
                        <ContextMenu
                          event="click"
                          position="bottomright"
                          softClick={false}
                          offsetY={4}
                          menu={
                            <MenuItemList>
                              <MenuItem
                                onClick={() => {
                                  downloadCSV();
                                }}
                              >
                                Assign Roles &amp; Users
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  downloadLineDetailsCSV();
                                }}
                              >
                                Update Line Details
                              </MenuItem>
                            </MenuItemList>
                          }
                        >
                          <button className={styles.downloadButton}>
                            <div className={styles.buttonSvg}>
                              <DownloadButtonSvg />
                            </div>
                            <span className={styles.templateText}>Template</span>
                          </button>
                        </ContextMenu>
                      ) : (
                        <button className={styles.downloadButton} onClick={() => downloadCSV()}>
                          <div className={styles.buttonSvg}>
                            <DownloadButtonSvg />
                          </div>
                          <span className={styles.templateText}>Template</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {selected.length > 0 && !isCategoryNurseCall && (
                  <div
                    className={styles.unassignUsersButton}
                    onClick={() => unassignUsersOrRoles(selected)}
                  >
                    Unassign {category === PBX_USER_LINES_TAB ? 'Users' : 'Roles'}
                  </div>
                )}
                {selected.length > 0 && isCategoryNurseCall && (
                  <div
                    className={styles.unassignUsersButton}
                    onClick={() => unassignUsersOrRoles(selected)}
                  >
                    Remove Users
                  </div>
                )}
              </div>
              {!isCategoryNurseCall && (
                <div className={styles.sortByContainer}>
                  <span className={styles.sortByLabel}>Sort by</span>
                  <Dropdown<SortValue>
                    dataTestId="pbx-sort-by"
                    options={SORT_OPTIONS}
                    defaultValue={sortDropdownValue}
                    onChange={handleSortChange}
                    customCssClass="snug"
                  />
                </div>
              )}
              <div>
                <UserRoleAssigneeTable
                  category={category}
                  pbxSelfServiceEnabled={pbxSelfServiceEnabled}
                  getAllPbxLinesInOrg={getAllPbxLinesInOrg}
                  onLineDetailsSaved={refreshCurrentPage}
                  organizationId={currentOrganizationId}
                  results={pbxLinesList}
                  selected={selected}
                  setPbxLinesList={setPbxLinesList}
                  setSelected={setSelected}
                  toggleSelected={toggleSelected}
                />
              </div>
            </div>
            {maxPages > 1 && (
              <Pagination
                currentPage={pageNumber}
                totalPages={maxPages}
                onPageChange={(page) => {
                  setPageNumber(page);
                  getAllPbxLinesInOrg({
                    category,
                    existingLines: [],
                    pageNumber: page,
                    search: isSearchActive ? extensionSearch.trim() : undefined,
                    ...sortRequestParamsFor(sortState),
                  });
                }}
              />
            )}
          </>
        )}
      </div>
    </ViewContainer>
  );
}

export default mobxInjectSelect<TabProps, MobxProps>({
  messengerStore: ['pbxSelfServiceEnabled'],
  modalStore: ['openModal'],
  userStore: ['findUser'],
})(PBX);
